import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import Papa from 'papaparse'
import Swal from 'sweetalert2'
import exportIcon from 'BoostXpressAdmin/Assets/Icon/Stations/export.svg'
import AttendantsCommissionTable, {
  AttendantsTableRow,
} from './components/AttendantsCommissionTable'
import CreateAttendantModal from './components/CreateAttendantModal'
import StationStaffTable from './components/StationStaffTable'
import { referralBXService } from 'BoostXpressAdmin/Services/referralBXService'
import { userBXService } from 'BoostXpressAdmin/Services/userBXService'
import fillingStationsService from 'BoostXpressAdmin/Services/fillingStationsService'
import {
  StationAttendant,
  StationAttendantsStats,
  stationAttendantsBXService,
} from 'BoostXpressAdmin/Services/stationAttendantsBXService'
import {
  StationAssignment,
  StationStaffDirectoryUser,
  StationStaffRole,
  stationStaffService,
} from 'BoostXpressAdmin/Services/stationStaffService'
import { handleError, handleSuccess } from 'Helpers'
import './Attendants.scss'

const formatMajorNaira = (value: number) => {
  const formatted = new Intl.NumberFormat('en-NG', { maximumFractionDigits: 0 }).format(value ?? 0)
  return `₦${formatted}`
}

type ReferralSummary = {
  totalReferrals?: number
  totalSuccessful?: number
  totalBonusPaidMajor?: number
  totalPending?: number
}

const Attendants = () => {
  const navigate = useNavigate()
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const [activeTab, setActiveTab] = useState<'attendants' | 'station-staff'>('attendants')

  const [stats, setStats] = useState<StationAttendantsStats | null>(null)
  const [isLoadingStats, setIsLoadingStats] = useState(false)

  const [referralSummary, setReferralSummary] = useState<ReferralSummary | null>(null)
  const [isLoadingReferralSummary, setIsLoadingReferralSummary] = useState(false)

  const [attendants, setAttendants] = useState<StationAttendant[]>([])
  const [isLoadingAttendants, setIsLoadingAttendants] = useState(false)
  const [page, setPage] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const limit = 25
  const [total, setTotal] = useState(0)

  const [disabled, setDisabled] = useState('')
  const [staffUsers, setStaffUsers] = useState<StationStaffDirectoryUser[]>([])
  const [staffAssignments, setStaffAssignments] = useState<StationAssignment[]>([])
  const [stationLabels, setStationLabels] = useState<Record<string, string>>({})
  const stationLabelsRef = useRef<Record<string, string>>({})
  const [isLoadingStaff, setIsLoadingStaff] = useState(false)
  const [staffError, setStaffError] = useState('')
  const [staffPage, setStaffPage] = useState(1)
  const [staffSearchTerm, setStaffSearchTerm] = useState('')
  const [staffRoleFilter, setStaffRoleFilter] = useState<'' | StationStaffRole>('')
  const [removingAssignmentId, setRemovingAssignmentId] = useState('')
  const [updatingStaffUserId, setUpdatingStaffUserId] = useState('')
  const staffLimit = 25

  const loadStats = async () => {
    try {
      setIsLoadingStats(true)
      const res = await stationAttendantsBXService.stats()
      setStats(res.data?.data ?? null)
    } catch (e) {
      setStats(null)
    } finally {
      setIsLoadingStats(false)
    }
  }

  const loadReferralSummary = async () => {
    try {
      setIsLoadingReferralSummary(true)
      const res = await referralBXService.summary()
      setReferralSummary(res.data?.data ?? null)
    } catch (e) {
      setReferralSummary(null)
    } finally {
      setIsLoadingReferralSummary(false)
    }
  }

  const loadAttendants = async () => {
    setIsLoadingAttendants(true)
    await stationAttendantsBXService
      .list({
        page: page,
        limit,
        sortOrder: 'ASC',
        search: searchTerm.trim() || undefined,
        disabled: disabled || undefined,
      })
      .then(
        (res) => {
          setAttendants(res.data?.data ?? [])

          setTotal(res.data?.meta?.total ?? 0)
          setPage(res.data?.meta?.page ?? 1)

          setIsLoadingAttendants(false)
        },
        () => {
          setAttendants([])

          setIsLoadingAttendants(false)
        },
      )
  }

  const loadStationStaff = async () => {
    try {
      setIsLoadingStaff(true)
      setStaffError('')
      const [users, assignments] = await Promise.all([
        stationStaffService.listAllDirectoryUsers(),
        stationStaffService.listAllAssignments(),
      ])
      setStaffUsers(users)
      setStaffAssignments(assignments)

      const stationIds = Array.from(
        new Set(
          assignments
            .map((assignment) => assignment.stationId)
            .filter((stationId) => !stationLabelsRef.current[stationId]),
        ),
      )
      const stationResults = await Promise.allSettled(
        stationIds.map(async (stationId) => ({
          stationId,
          response: await fillingStationsService.getById(stationId),
        })),
      )
      const loadedLabels: Record<string, string> = {}

      stationResults.forEach((result) => {
        if (result.status !== 'fulfilled') return
        const { stationId, response } = result.value
        const station = response.data
        if (!station) return
        loadedLabels[stationId] =
          [station.businessName, station.branchName].filter(Boolean).join(': ') || stationId
      })

      if (Object.keys(loadedLabels).length) {
        stationLabelsRef.current = { ...stationLabelsRef.current, ...loadedLabels }
        setStationLabels(stationLabelsRef.current)
      }
    } catch {
      setStaffUsers([])
      setStaffAssignments([])
      setStaffError('Station staff could not be loaded. Please try again.')
    } finally {
      setIsLoadingStaff(false)
    }
  }

  const removeStaffAssignment = async (assignment: StationAssignment) => {
    const result = await Swal.fire({
      title: 'Remove station assignment?',
      text: `${assignment.userFirstName} ${assignment.userLastName} will no longer be the ${assignment.role === 'STATION_MANAGER' ? 'manager' : 'associate'} for this station.`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#eb5757',
      confirmButtonText: 'Remove assignment',
    })

    if (!result.isConfirmed) return

    try {
      setRemovingAssignmentId(assignment.resourceId)
      await stationStaffService.deleteAssignment(assignment.resourceId)
      handleSuccess('Station assignment removed successfully.')
      await loadStationStaff()
    } catch (error) {
      handleError(error as Parameters<typeof handleError>[0])
    } finally {
      setRemovingAssignmentId('')
    }
  }

  const toggleStaffUserStatus = async (
    user: StationStaffDirectoryUser,
    hasAssignment: boolean,
  ) => {
    const isDisabled = Boolean(user.disabledAt)
    const name = `${user.firstName} ${user.lastName}`.trim() || user.email
    const result = await Swal.fire({
      title: `${isDisabled ? 'Enable' : 'Disable'} staff account?`,
      text: isDisabled
        ? `${name} will regain access to their station staff account.`
        : `${name} will no longer be able to sign in.${
            hasAssignment ? ' Their station assignment will remain in place.' : ''
          }`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: isDisabled ? '#186aff' : '#eb5757',
      confirmButtonText: `${isDisabled ? 'Enable' : 'Disable'} account`,
    })

    if (!result.isConfirmed) return

    try {
      setUpdatingStaffUserId(user.resourceId)
      if (isDisabled) await userBXService.enable(user.resourceId)
      else await userBXService.disable(user.resourceId)
      handleSuccess(`Staff account ${isDisabled ? 'enabled' : 'disabled'} successfully.`)
      await loadStationStaff()
    } catch (error) {
      handleError(error as Parameters<typeof handleError>[0])
    } finally {
      setUpdatingStaffUserId('')
    }
  }

  useEffect(() => {
    loadStats()
    loadReferralSummary()
  }, [])

  useEffect(() => {
    if (activeTab === 'attendants') loadAttendants()
  }, [activeTab, page, searchTerm, disabled])

  useEffect(() => {
    if (activeTab === 'station-staff') loadStationStaff()
  }, [activeTab])

  useEffect(() => {
    setPage(1)
  }, [searchTerm])

  const tableRows: AttendantsTableRow[] = useMemo(() => {
    const attendantRows = (attendants ?? []).map((a) => {
      const stationLabel = a.fillingStation
        ? `${a.fillingStation.businessName}${a.fillingStation.city ? `, ${a.fillingStation.city}` : ''}`
        : '—'
      const name = `${a.firstName ?? ''} ${a.lastName ?? ''}`.trim() || '—'
      const status: 'Active' | 'Inactive' = a.disabledAt ? 'Inactive' : 'Active'

      return {
        id: a.resourceId,
        name,
        email: a.email,
        phone: a.phone,
        station: stationLabel,
        role: a.role,
        referralCode: a.referralCode,
        createdAt: a.createdAt,
        lastLoginAt: a.lastLoginAt,
        status,
      }
    })

    return attendantRows
  }, [attendants])

  const exportAttendantsCsv = () => {
    const csv = Papa.unparse(
      tableRows.map((r) => ({
        name: r.name,
        email: r.email,
        phone: r.phone,
        country: 'Nigeria',
        station: r.station,
        referralCode: r.referralCode,
        status: r.status,
      })),
    )

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `attendants_${new Date().toISOString().slice(0, 10)}.csv`
    document.body.appendChild(a)
    a.click()
    a.remove()
    window.URL.revokeObjectURL(url)
  }

  const statsCards = useMemo(() => {
    if (!stats) {
      return [
        {
          title: 'Total Attendants',
          value: '—',
          subtitle: 'All attendants',
          variant: 'accent',
          textColor: 'text-white',
        },
        {
          title: 'Total Pending',
          value: '—',
          subtitle: 'Pending withdrawals',
          variant: 'default',
          textColor: 'text-dark',
        },
        {
          title: 'Total Completed',
          value: '—',
          subtitle: 'Completed withdrawals',
          variant: 'default',
          textColor: 'text-dark',
        },
        { title: 'Total Paid Out', value: '—', subtitle: 'Earnings paid out', variant: 'default' },
      ]
    }

    return [
      {
        title: 'Total Attendants',
        value: String(stats.totalAttendants ?? 0),
        subtitle: 'All attendants',
        variant: 'accent',
        textColor: 'text-white',
      },
      {
        title: 'Total Pending',
        value: formatMajorNaira(stats.totalPendingWithdrawalMajor ?? 0),
        subtitle: 'Pending withdrawals',
        variant: 'default',
        textColor: '',
      },
      {
        title: 'Total Completed',
        value: formatMajorNaira(stats.totalCompletedWithdrawalMajor ?? 0),
        subtitle: 'Completed withdrawals',
        variant: 'default',
        textColor: '',
      },
      {
        title: 'Total Paid Out',
        value: formatMajorNaira(stats.totalEarningsPaidOutMajor ?? 0),
        subtitle: 'Earnings paid out',
        variant: 'default',
        textColor: '',
      },
    ]
  }, [stats])

  const overviewItems = useMemo(() => {
    if (!referralSummary) {
      return [
        { title: 'New Referrals', value: '—', subtitle: '—' },
        { title: 'Active Referrals', value: '—', subtitle: '—' },
        { title: 'Rebates Paid', value: '—', subtitle: '—' },
        { title: 'Pending Rebates', value: '—', subtitle: '—' },
      ]
    }

    return [
      { title: 'New Referrals', value: String(referralSummary.totalReferrals ?? 0), subtitle: '' },
      {
        title: 'Active Referrals',
        value: String(referralSummary.totalSuccessful ?? 0),
        subtitle: '',
      },
      {
        title: 'Rebates Paid',
        value: `₦${Number(referralSummary.totalBonusPaidMajor ?? 0).toLocaleString()}`,
        subtitle: '',
      },
      { title: 'Pending Rebates', value: String(referralSummary.totalPending ?? 0), subtitle: '' },
    ]
  }, [referralSummary])

  const staffSummaryCards = useMemo(() => {
    const assignedUserIds = new Set(
      staffAssignments.map((assignment) => assignment.userResourceId),
    )
    const assignedManagerIds = new Set(
      staffAssignments
        .filter((assignment) => assignment.role === 'STATION_MANAGER')
        .map((assignment) => assignment.userResourceId),
    )
    const assignedAssociateIds = new Set(
      staffAssignments
        .filter((assignment) => assignment.role === 'STATION_ASSOCIATE')
        .map((assignment) => assignment.userResourceId),
    )

    return [
      {
        title: 'Assigned Station Staff',
        value: String(assignedUserIds.size),
        subtitle: 'Distinct users with station assignments',
      },
      {
        title: 'Assigned Managers',
        value: String(assignedManagerIds.size),
        subtitle: 'Distinct users assigned as managers',
      },
      {
        title: 'Assigned Associates',
        value: String(assignedAssociateIds.size),
        subtitle: 'Distinct users assigned as associates',
      },
    ]
  }, [staffAssignments])

  return (
    <div className='bx-attendants-page'>
      <div className='bx-attendants-page__header'>
        <h2 className='bx-attendants-page__title'>Station Users</h2>
      </div>

      <div className='bx-attendants-tabs' role='tablist' aria-label='Station user type'>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === 'attendants'}
          className={`bx-attendants-tabs__button${activeTab === 'attendants' ? ' bx-attendants-tabs__button--active' : ''}`}
          onClick={() => setActiveTab('attendants')}
        >
          Attendants
        </button>
        <button
          type='button'
          role='tab'
          aria-selected={activeTab === 'station-staff'}
          className={`bx-attendants-tabs__button${activeTab === 'station-staff' ? ' bx-attendants-tabs__button--active' : ''}`}
          onClick={() => setActiveTab('station-staff')}
        >
          Station Staff
        </button>
      </div>

      {activeTab === 'attendants' ? (
        <div className='bx-attendants-tab-panel' role='tabpanel'>
          <div className='row g-3 bx-attendants-page__stats'>
            {statsCards.map((card) => (
              <div key={card.title} className='col-12 col-md-6 col-xl-3'>
                <div
                  className={`bx-summary-card ${card.variant === 'accent' ? 'bx-summary-card--accent' : ''}`}
                >
                  <div className={`bx-summary-card__label ${card.textColor}`}>{card.title}</div>
                  <div className={`bx-summary-card__value ${card.textColor}`}>
                    {isLoadingStats ? 'Loading...' : card.value}
                  </div>
                  <div className={`bx-summary-card__sub ${card.textColor}`}>{card.subtitle}</div>
                </div>
              </div>
            ))}
          </div>

          <section className='bx-overview-card'>
            <div className='bx-overview-card__header'>
              <div className='bx-overview-card__title'>Referral Overview</div>
              <button
                type='button'
                className='bx-overview-card__export'
                onClick={exportAttendantsCsv}
                disabled={isLoadingAttendants || !tableRows.length}
              >
                <img src={exportIcon} alt='' className='bx-overview-card__export-icon' />
                Export CSV
              </button>
            </div>

            <div className='bx-overview-card__grid'>
              {(isLoadingReferralSummary ? [] : overviewItems).map((item) => (
                <div key={item.title} className='bx-overview-card__item'>
                  <div className='bx-overview-card__item-title'>{item.title}</div>
                  <div className='bx-overview-card__item-value'>{item.value}</div>
                  <div className='bx-overview-card__item-subtitle'>{item.subtitle}</div>
                </div>
              ))}
              {isLoadingReferralSummary ? (
                <div className='bx-overview-card__item bx-overview-card__item--loading'>
                  Loading...
                </div>
              ) : null}
            </div>
          </section>

          <AttendantsCommissionTable
            title='All Attendants'
            rows={tableRows}
            isLoading={isLoadingAttendants}
            onPrimaryAction={() => setIsCreateOpen(true)}
            onViewAction={(attendantId) => navigate(`/boostxpress/attendants/${attendantId}`)}
            searchTerm={searchTerm}
            onSearchTermChange={setSearchTerm}
            page={page}
            total={total}
            limit={limit}
            setPage={setPage}
            reloadList={loadAttendants}
            disable={disabled}
            setDisable={setDisabled}
          />
        </div>
      ) : (
        <div className='bx-attendants-tab-panel' role='tabpanel'>
          <div className='row g-3 bx-attendants-page__stats'>
            {staffSummaryCards.map((card, index) => (
              <div key={card.title} className='col-12 col-md-4'>
                <div className={`bx-summary-card${index === 0 ? ' bx-summary-card--accent' : ''}`}>
                  <div className={`bx-summary-card__label${index === 0 ? ' text-white' : ''}`}>
                    {card.title}
                  </div>
                  <div className={`bx-summary-card__value${index === 0 ? ' text-white' : ''}`}>
                    {isLoadingStaff ? 'Loading...' : card.value}
                  </div>
                  <div className={`bx-summary-card__sub${index === 0 ? ' text-white' : ''}`}>
                    {card.subtitle}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <StationStaffTable
            users={staffUsers}
            assignments={staffAssignments}
            stationLabels={stationLabels}
            isLoading={isLoadingStaff}
            error={staffError}
            page={staffPage}
            limit={staffLimit}
            searchTerm={staffSearchTerm}
            roleFilter={staffRoleFilter}
            removingAssignmentId={removingAssignmentId}
            updatingUserId={updatingStaffUserId}
            onPageChange={setStaffPage}
            onSearchTermChange={(value) => {
              setStaffSearchTerm(value)
              setStaffPage(1)
            }}
            onRoleFilterChange={(value) => {
              setStaffRoleFilter(value)
              setStaffPage(1)
            }}
            onAddUser={() => setIsCreateOpen(true)}
            onManageStation={(stationId) => navigate(`/boostxpress/stations/${stationId}/edit`)}
            onChooseStation={() => navigate('/boostxpress/stations')}
            onRemoveAssignment={removeStaffAssignment}
            onToggleUserStatus={toggleStaffUserStatus}
            onRetry={loadStationStaff}
          />
        </div>
      )}

      <CreateAttendantModal
        isOpen={isCreateOpen}
        initialRole={activeTab === 'station-staff' ? 'Manager' : 'Attendant'}
        onClose={() => setIsCreateOpen(false)}
        onSuccess={() => {
          loadStats()
          loadAttendants()
          loadStationStaff()
        }}
      />
    </div>
  )
}

export default Attendants
